import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState, useCallback, useRef } from 'react';
import { 
  getActionForStatePath, 
  hasAutoAction, 
  getStatePathFromSelector, 
  isEmptyState 
} from './useAutoRefetchSelectorConfig';

/**
 * Enhanced useAutoRefetchSelector Hook
 * Automatically handles state selection, empty state detection, and action dispatching
 * Uses config-based mapping for state paths to action creators
 * 
 * @param {Function} selector - Redux state selector function
 * @param {Object} options - Optional configuration (for backward compatibility)
 * @returns {Object} - { data, isLoading, error, refetch }
 */
export const useAutoRefetchSelector = (selector, options = {}) => {
  const dispatch = useDispatch();
  const data = useSelector(selector);
  
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const mountedRef = useRef(true);
  
  // Get state path and corresponding action from config
  const statePath = getStatePathFromSelector(selector);
  const actionCreator = getActionForStatePath(statePath);
  const hasAction = hasAutoAction(statePath);
  
  // Check if data is empty using config-based logic
  const isEmpty = isEmptyState(data);
  
  // Enhanced refetch function
  const refetch = useCallback(async () => {
    if (!actionCreator) {
      console.warn(`No action configured for state path: ${statePath}`);
      return Promise.resolve(null);
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      console.log(`Refetching data for state path: ${statePath}`);
      
      // Dispatch the action
      await dispatch(actionCreator());
      
      console.log(`Successfully dispatched action for: ${statePath}`);
    } catch (err) {
      console.error(`Error refetching data for ${statePath}:`, err);
      setError(err);
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [actionCreator, dispatch, statePath]);
  
  // Auto-refetch on mount if data is empty
  useEffect(() => {
    if (!hasInitialized && isEmpty && hasAction) {
      console.log(`Auto-refetch on mount for empty state: ${statePath}`);
      refetch();
      setHasInitialized(true);
    } else if (!hasInitialized) {
      setHasInitialized(true);
    }
  }, [hasInitialized, isEmpty, hasAction, statePath, refetch]);
  
  // Auto-refetch when data becomes empty (after initial load)
  useEffect(() => {
    if (hasInitialized && isEmpty && hasAction && !isLoading) {
      console.log(`Auto-refetch for empty state detected: ${statePath}`);
      refetch();
    }
  }, [hasInitialized, isEmpty, hasAction, isLoading, statePath, refetch]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  return {
    data,
    isLoading,
    error,
    refetch,
    // Additional metadata for debugging
    meta: {
      statePath,
      hasAction,
      isEmpty,
      hasInitialized
    }
  };
};

/**
 * Simplified useAutoRefetchState Hook
 * For backward compatibility with existing components
 * 
 * @param {string} reducerName - Name of the reducer
 * @param {string} stateName - Name of the state property
 * @param {Function} actionCreator - Action creator function (optional, uses config if not provided)
 * @param {Object} options - Optional configuration
 * @returns {Object} - { data, isLoading, error, refetch }
 */
export const useAutoRefetchState = (reducerName, stateName, actionCreator = null, options = {}) => {
  const dispatch = useDispatch(); // Add missing dispatch hook
  
  // Create selector from reducer and state names
  const selector = useCallback((state) => {
    return state[reducerName]?.[stateName];
  }, [reducerName, stateName]);
  
  // Use enhanced useAutoRefetchSelector
  const result = useAutoRefetchSelector(selector, options);
  
  // Override action creator if provided (for backward compatibility)
  const customRefetch = useCallback(async () => {
    if (actionCreator) {
      try {
        result.isLoading = true;
        await dispatch(actionCreator());
        console.log(`Custom refetch completed for ${reducerName}.${stateName}`);
      } catch (err) {
        console.error(`Custom refetch error for ${reducerName}.${stateName}:`, err);
        throw err;
      }
    } else {
      return result.refetch();
    }
  }, [actionCreator, result, reducerName, stateName, dispatch]);
  
  return {
    ...result,
    refetch: actionCreator ? customRefetch : result.refetch
  };
};

/**
 * Hook for components that need multiple auto-refetch selectors
 * Provides batch refetch capabilities
 * 
 * Note: This hook should be used with a fixed array of selectors.
 * For dynamic selectors, use individual useAutoRefetchSelector calls.
 * 
 * @param {Object} selectorConfigs - Object with selector configurations
 * @returns {Object} - { data, refetchAll, isAnyLoading }
 */
export const useMultipleAutoRefetchSelectors = (selectorConfigs) => {
  // Since we can't call hooks in loops, this hook expects a fixed set of selectors
  // Components should call individual useAutoRefetchSelector hooks instead
  
  console.warn('useMultipleAutoRefetchSelectors is deprecated. Use individual useAutoRefetchSelector calls in your component instead.');
  
  // Return empty structure for backward compatibility
  return {
    data: {},
    refetchAll: useCallback(() => {
      console.log('useMultipleAutoRefetchSelectors is deprecated - use individual hooks');
    }, []),
    isAnyLoading: false
  };
};

export default useAutoRefetchSelector;
